import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { createTrainerAudio } from '../components/TrainerFeedback/audio';

export const TRAINER_FEEDBACK_STORAGE_KEY = 'omahapro.trainerFeedback.v1';
const PREFERENCES_EVENT = 'omahapro:trainer-feedback-preferences';
const DEFAULTS = { soundEnabled: false, animationsEnabled: true };
const MILESTONES = [5, 10, 20];

const readPreferences = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(TRAINER_FEEDBACK_STORAGE_KEY));
    return {
      soundEnabled: typeof saved?.soundEnabled === 'boolean' ? saved.soundEnabled : DEFAULTS.soundEnabled,
      animationsEnabled: typeof saved?.animationsEnabled === 'boolean' ? saved.animationsEnabled : DEFAULTS.animationsEnabled,
    };
  } catch (_) {
    return { ...DEFAULTS };
  }
};

const prefersReducedMotion = () => (
  typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

export default function useTrainerFeedback({ sessionKey } = {}) {
  const [preferences, setPreferences] = useState(readPreferences);
  const preferencesRef = useRef(preferences);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;
  const [streak, setStreak] = useState(0);
  const streakRef = useRef(0);
  const sessionTotalsRef = useRef({ hands: 0, correct: 0, bestStreak: 0 });
  const [sessionRecap, setSessionRecap] = useState(null);
  const [event, setEvent] = useState(null);
  const eventId = useRef(0);
  const [dealId, setDealId] = useState(0);
  const [dealAnimated, setDealAnimated] = useState(false);
  const handRef = useRef(null);
  const answeredRef = useRef(false);
  const audioRef = useRef(null);
  const audioUnlockRef = useRef(null);
  const audioPlaybackIdRef = useRef(0);

  const getAudio = useCallback(() => {
    if (!audioRef.current) audioRef.current = createTrainerAudio();
    return audioRef.current;
  }, []);

  const prepareAudio = useCallback(() => {
    if (!preferencesRef.current.soundEnabled) return Promise.resolve(false);
    const attempt = getAudio().unlock();
    audioUnlockRef.current = attempt;
    return attempt;
  }, [getAudio]);

  const stopAudio = useCallback(() => {
    audioPlaybackIdRef.current += 1;
    audioUnlockRef.current = null;
    if (audioRef.current) audioRef.current.stop();
  }, []);

  useEffect(() => {
    const sync = (e) => {
      if (e.type === 'storage' && e.key !== TRAINER_FEEDBACK_STORAGE_KEY && e.key !== null) return;
      const next = e.type === PREFERENCES_EVENT ? e.detail : readPreferences();
      if (next.animationsEnabled !== preferencesRef.current.animationsEnabled) {
        setEvent(null);
        setDealAnimated(false);
      }
      preferencesRef.current = next;
      setPreferences(next);
      if (!next.soundEnabled) stopAudio();
    };
    // A restored sound preference still needs a real browser gesture to unlock audio.
    const unlock = () => { prepareAudio(); };
    window.addEventListener('storage', sync);
    window.addEventListener(PREFERENCES_EVENT, sync);
    window.addEventListener('pointerdown', unlock, { passive: true, capture: true });
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(PREFERENCES_EVENT, sync);
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      stopAudio();
      if (audioRef.current) audioRef.current.dispose();
      audioRef.current = null;
    };
  }, [prepareAudio, stopAudio]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      if (media.matches !== reducedMotionRef.current) {
        setEvent(null);
        setDealAnimated(false);
      }
      setReducedMotion(media.matches);
    };
    update();
    if (media.addEventListener) media.addEventListener('change', update);
    else if (media.addListener) media.addListener(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', update);
      else if (media.removeListener) media.removeListener(update);
    };
  }, []);

  const updatePreference = useCallback((key) => {
    const next = { ...preferencesRef.current, [key]: !preferencesRef.current[key] };
    preferencesRef.current = next;
    setPreferences(next);
    try {
      window.localStorage.setItem(TRAINER_FEEDBACK_STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // Private browsing/storage limits must never interrupt a hand.
    }
    window.dispatchEvent(new CustomEvent(PREFERENCES_EVENT, { detail: next }));
    if (key === 'soundEnabled') {
      if (next.soundEnabled) prepareAudio();
      else stopAudio();
    }
  }, [prepareAudio, stopAudio]);

  const onToggleSound = useCallback(() => updatePreference('soundEnabled'), [updatePreference]);
  const onToggleAnimations = useCallback(() => {
    updatePreference('animationsEnabled');
    setEvent(null);
    setDealAnimated(false);
  }, [updatePreference]);

  const clearActiveFeedback = useCallback(() => {
    streakRef.current = 0;
    setStreak(0);
    setEvent(null);
    setDealAnimated(false);
    stopAudio();
  }, [stopAudio]);

  const resetSession = useCallback(() => {
    sessionTotalsRef.current = { hands: 0, correct: 0, bestStreak: 0 };
    setSessionRecap(null);
    clearActiveFeedback();
    // Keep the current hand's answered flag: Reset must not let it score twice.
  }, [clearActiveFeedback]);

  const finishSession = useCallback(() => {
    const { hands, correct, bestStreak } = sessionTotalsRef.current;
    if (hands > 0) {
      setSessionRecap({
        hands, correct, accuracy: Math.round((correct / hands) * 1000) / 10, bestStreak,
      });
    }
    sessionTotalsRef.current = { hands: 0, correct: 0, bestStreak: 0 };
    // Stop closes the current hand, including a timer/answer that arrives afterwards.
    answeredRef.current = true;
    clearActiveFeedback();
  }, [clearActiveFeedback]);

  const dismissRecap = useCallback(() => setSessionRecap(null), []);

  useEffect(() => { resetSession(); }, [sessionKey, resetSession]);

  const beginHand = useCallback((handKey) => {
    if (!handKey || handRef.current === handKey) return;
    handRef.current = handKey;
    answeredRef.current = false;
    setDealAnimated(preferencesRef.current.animationsEnabled && !reducedMotionRef.current);
    setDealId((value) => value + 1);
  }, []);

  const recordAnswer = useCallback((correct, handKey) => {
    if (!handKey || handRef.current !== handKey || answeredRef.current) return false;
    answeredRef.current = true;
    const nextStreak = correct ? streakRef.current + 1 : 0;
    streakRef.current = nextStreak;
    setStreak(nextStreak);
    const totals = sessionTotalsRef.current;
    sessionTotalsRef.current = {
      hands: totals.hands + 1,
      correct: totals.correct + (correct ? 1 : 0),
      bestStreak: Math.max(totals.bestStreak, nextStreak),
    };
    let kind = 'wrong';
    if (correct) kind = MILESTONES.includes(nextStreak) ? 'milestone' : 'correct';
    eventId.current += 1;
    setEvent({ id: eventId.current, kind });
    audioPlaybackIdRef.current += 1;
    const playbackId = audioPlaybackIdRef.current;
    if (preferencesRef.current.soundEnabled) {
      const audio = getAudio();
      const requestedAt = Date.now();
      if (audio.play(kind) === false && audioUnlockRef.current) {
        // iOS/other suspended contexts may finish gesture-driven resume after grading.
        // Retry only this fresh result; mute, Reset and a newer answer invalidate it.
        audioUnlockRef.current.then((ready) => {
          if (ready && audioPlaybackIdRef.current === playbackId
            && preferencesRef.current.soundEnabled && audioRef.current === audio
            && Date.now() - requestedAt <= 300) audio.play(kind);
        }).catch(() => { /* Optional audio must never interrupt training. */ });
      }
    }
    return true;
  }, [getAudio]);

  return {
    ...preferences,
    reducedMotion,
    motionEnabled: preferences.animationsEnabled && !reducedMotion,
    streak,
    event,
    dealId,
    dealAnimated,
    onToggleSound,
    onToggleAnimations,
    prepareAudio,
    resetSession,
    sessionRecap,
    finishSession,
    dismissRecap,
    beginHand,
    recordAnswer,
  };
}
